import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MenuesService } from '@services/menues/menues.service';
import { StorageService } from '@services/shared/storage.service';
import { AuthService } from '@services/auth/auth.service';
import { TiposWebKeys } from '@enum/tiposweb-keys.enum';
import { DiccionarioKeys } from '@enum/idiomas-diccionario-keys.enum';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';
import { MenuTreeNode } from '@interfaces/interface-bakend/seguridad/acl/menu.interface';

@Component({
    selector: 'app-menu-edicion',
    templateUrl: './menu-edicion.component.html',
    styleUrls: ['./menu-edicion.component.scss'],
})
export class MenuEdicionComponent implements OnInit, OnDestroy {

    form: FormGroup;
    flagLoaded: boolean = false;
    modoCreacion: boolean = true;
    menuId: string | null = null;
    allMenues: any[] = [];
    nombreTraducido: string = '';

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private menuesService: MenuesService,
        private storageService: StorageService,
        private authService: AuthService,
        private modalCustom: PopUpModalCustomService,
    ) {
        this.form = this.fb.group({
            name: ['', Validators.required],
            redirectTo: ['', Validators.required],
            tipo: ['WEB', Validators.required],
            icon: [''],
            parent: [null],
            order: [1, [Validators.min(1)]],
        });
    }

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
    }

    async ngOnInit(): Promise<void> {
        this.menuId = this.route.snapshot.queryParamMap.get('id');
        this.modoCreacion = !this.menuId;

        const tree = await this.menuesService.getMenuTree(
            TiposWebKeys.WEB,
            DiccionarioKeys.I18NFILE,
            'es',
        );
        this.allMenues = this.flatten(tree || []);

        if (this.menuId) {
            const menuResp: any = await this.menuesService.getItemMenue(this.menuId);
            const menu = menuResp;
            if (menu) {
                const rawName = menu.name || '';
                const nodo = this.allMenues.find((m) => m._id === this.menuId);
                this.nombreTraducido = (nodo && nodo.name) || this.limpiarNombre(rawName);
                this.form.patchValue({
                    name: rawName,
                    redirectTo: menu.redirectTo || '',
                    tipo: menu.tipo || 'WEB',
                    icon: menu.icon || '',
                    parent: menu.parent || null,
                    order: menu.order || 1,
                });
            }
        }

        this.flagLoaded = true;
    }

    private flatten(nodes: MenuTreeNode[]): MenuTreeNode[] {
        const flat: MenuTreeNode[] = [];
        const walk = (list: MenuTreeNode[]) => {
            for (const node of list) {
                flat.push(node);
                walk(node.children || []);
            }
        };
        walk(nodes);
        return flat;
    }

    get opcionesPadre(): any[] {
        const filtered = this.allMenues.filter((m) => {
            if (this.menuId && m._id === this.menuId) { return false; }
            return true;
        });
        filtered.sort((a, b) => (a.level || 0) - (b.level || 0));
        return filtered;
    }

    indentarMenu(menu: any): string {
        const level = menu.level || 0;
        return '&nbsp;&nbsp;'.repeat(Math.max(0, level - 1));
    }

    limpiarNombre(name: string): string {
        return name.replace(/^general-service-signomedico\./, '');
    }

    nombreTraducidoMenu(menu: any): string {
        return (menu && menu.name) || this.limpiarNombre((menu && menu.rawName) || '');
    }

    async guardar(): Promise<void> {
        if (this.form.invalid) { return; }

        const formData = this.form.value;
        const parentId = formData.parent;

        const payload: any = {
            name: formData.name,
            redirectTo: formData.redirectTo,
            tipo: formData.tipo,
            icon: formData.icon || 'string',
            path: 'string',
            item: 'string',
            parent: parentId || null,
            order: formData.order || 1,
        };

        if (parentId) {
            const parentMenu = this.allMenues.find((m) => m._id === parentId);
            payload.level = (parentMenu?.level || 1) + 1;
        } else {
            payload.level = 1;
        }

        let result: any;
        if (this.modoCreacion) {
            result = await this.menuesService.createMenu(payload);
        } else {
            result = await this.menuesService.updateMenu(this.menuId!, payload);
        }

        if (result) {
            const user = await this.authService.getUserLogon();
            this.storageService.clearMenuCache('es', user?.perfil?._id || '');

            this.modalCustom.abrirPopup({
                titulo: this.modoCreacion ? 'Menú creado' : 'Menú actualizado',
                mensaje: this.modoCreacion
                    ? 'El menú se creó correctamente.'
                    : 'El menú se actualizó correctamente.',
                tipo: 'success',
                accionConfirmar: () => this.volverAlListado(),
                accionCerrar: () => this.volverAlListado(),
            });
        } else {
            this.modalCustom.abrirPopup({
                titulo: 'Error al guardar',
                mensaje: 'Ocurrió un error al guardar el menú. Intente nuevamente.',
                tipo: 'error',
                accionConfirmar: () => this.volverAlListado(),
                accionCerrar: () => this.volverAlListado(),
            });
        }
    }

    private volverAlListado(): void {
        this.router.navigateByUrl('/menues-administracion');
    }

    cancelar(): void {
        this.volverAlListado();
    }
}
